import { Injectable, inject } from '@angular/core';
import { LosPersistenceKvService } from '../../../core/indexed-db/los-persistence-kv.service';
import type {
  OpportunityView,
  OpportunityViewParams,
} from './opportunity-navigation.state';

const NAV_STORAGE_KEY = 'opportunity-shell-nav';
const DRAFT_KEY_PREFIX = 'opportunity-draft:';

/** IndexedDB `kv` keys written by the Opportunities module (nav, drafts, bureau, workflow aux). */
export const OPPORTUNITY_KV_SESSION_PREFIXES: readonly string[] = [
  DRAFT_KEY_PREFIX,
  NAV_STORAGE_KEY,
  'opportunityBureauReportId_',
  'opportunityCreditScore_',
  'opportunityIdProofType_',
  'opportunityIdProofNumber_',
  'wfBureauReportId_',
  'wfBureauCreditScore_',
  'wf-idProof-',
];
const NAV_VERSION = 1;
const DRAFT_VERSION = 1;

/** Default draft TTL — override in one place if product policy changes. */
export const OPPORTUNITY_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

const VALID_VIEWS = new Set<OpportunityView>([
  'list',
  'create',
  'detail',
  'customer-search',
  'credit-bureau',
]);

const SENSITIVE_FIELD_PATTERN =
  /password|passwd|otp|token|secret|credential|apikey|api_key/i;

function isPackPreverifiedPhoneEntry(key: string, mobile: unknown): boolean {
  return (
    key.trim().length > 0 &&
    typeof mobile === 'string' &&
    mobile.trim().length > 0
  );
}

export interface PersistedShellNav {
  version: number;
  view: OpportunityView;
  params: OpportunityViewParams;
  savedAt: number;
}

export interface OpportunityFormDraft {
  version: number;
  mode: 'create' | 'edit';
  opportunityId?: string;
  workflowInstanceId?: string;
  wfStep?: string;
  applicantType?: string;
  applicantStatus?: string;
  updatedAt: number;
  serverSeed?: {
    id: string;
    status?: string;
    opportunityNumber?: string;
  };
  values: OpportunityFormDraftValues;
}

export interface OpportunityFormDraftValues {
  packPhoneOtpVerified?: boolean;
  /** Verified mobile(s) by pack field code — restores OTP UI after navigation. */
  packPreverifiedPhones?: Record<string, string>;
  packFormValues?: Record<string, unknown>;
  legacyForms?: {
    basicDetails?: Record<string, unknown>;
    loanDetails?: Record<string, unknown>;
    sourceDetails?: Record<string, unknown>;
    addressDetails?: Record<string, unknown>;
    leadPriority?: Record<string, unknown>;
  };
  activeTab?: string;
}

@Injectable({ providedIn: 'root' })
export class OpportunityShellPersistenceService {
  private readonly kv = inject(LosPersistenceKvService);

  saveNavigationState(view: OpportunityView, params: OpportunityViewParams): void {
    if (!VALID_VIEWS.has(view)) {
      return;
    }
    const payload: PersistedShellNav = {
      version: NAV_VERSION,
      view,
      params: this.sanitizeNavParams(params),
      savedAt: Date.now(),
    };
    this.writeJson(NAV_STORAGE_KEY, payload);
  }

  async loadNavigationState(): Promise<PersistedShellNav | null> {
    const raw = await this.readJson<Partial<PersistedShellNav>>(NAV_STORAGE_KEY);
    if (!raw || raw.version !== NAV_VERSION) {
      this.removeKey(NAV_STORAGE_KEY);
      return null;
    }
    if (!raw.view || !VALID_VIEWS.has(raw.view)) {
      this.removeKey(NAV_STORAGE_KEY);
      return null;
    }
    return {
      version: NAV_VERSION,
      view: raw.view,
      params: this.sanitizeNavParams(raw.params ?? {}),
      savedAt: typeof raw.savedAt === 'number' ? raw.savedAt : 0,
    };
  }

  clearNavigationState(): void {
    this.removeKey(NAV_STORAGE_KEY);
  }

  saveFormDraft(key: string, draft: OpportunityFormDraft): void {
    if (!key.startsWith(DRAFT_KEY_PREFIX)) {
      return;
    }
    this.writeJson(key, {
      ...draft,
      version: DRAFT_VERSION,
      updatedAt: Date.now(),
    });
  }

  async loadFormDraft(key: string): Promise<OpportunityFormDraft | null> {
    if (!key.startsWith(DRAFT_KEY_PREFIX)) {
      return null;
    }
    const raw = await this.readJson<Partial<OpportunityFormDraft>>(key);
    if (!raw || raw.version !== DRAFT_VERSION) {
      this.removeKey(key);
      return null;
    }
    if (raw.mode !== 'create' && raw.mode !== 'edit') {
      this.removeKey(key);
      return null;
    }
    if (this.isExpired(raw.updatedAt)) {
      this.removeKey(key);
      return null;
    }
    return {
      version: DRAFT_VERSION,
      mode: raw.mode,
      opportunityId: raw.opportunityId,
      workflowInstanceId: raw.workflowInstanceId,
      wfStep: raw.wfStep,
      applicantType: raw.applicantType,
      applicantStatus: raw.applicantStatus,
      updatedAt: raw.updatedAt ?? 0,
      serverSeed: raw.serverSeed,
      values: this.sanitizeDraftValues(raw.values ?? {}),
    };
  }

  clearFormDraft(key: string): void {
    this.removeKey(key);
  }

  clearCreateDrafts(): void {
    void this.kv.removeKeysWithPrefix(`${DRAFT_KEY_PREFIX}create:`);
  }

  clearEditDraft(opportunityId: string): void {
    const id = opportunityId.trim();
    if (!id) {
      return;
    }
    this.removeKey(this.editDraftKey(id));
  }

  async clearAllOpportunitySession(): Promise<void> {
    this.clearNavigationState();
    await this.kv.removeKeysWithPrefixes([...OPPORTUNITY_KV_SESSION_PREFIXES]);
    this.kv.clearMemoryCache();
  }

  createDraftKey(workflowInstanceId: string): string {
    return `${DRAFT_KEY_PREFIX}create:${workflowInstanceId}`;
  }

  editDraftKey(opportunityId: string): string {
    return `${DRAFT_KEY_PREFIX}edit:${opportunityId.trim()}`;
  }

  /** Generic kv helpers for credit bureau / auxiliary Opportunity keys. */
  setAuxiliaryValue(key: string, value: string): void {
    this.kv.set(key, value);
  }

  getAuxiliaryValueCached(key: string): string | null {
    return this.kv.getCached(key);
  }

  async getAuxiliaryValue(key: string): Promise<string | null> {
    return this.kv.get(key);
  }

  removeAuxiliaryValue(key: string): void {
    this.kv.remove(key);
  }

  sanitizeFormFieldValues(
    values: Record<string, unknown> | null | undefined,
  ): Record<string, unknown> {
    if (!values) {
      return {};
    }
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(values)) {
      if (SENSITIVE_FIELD_PATTERN.test(key)) {
        continue;
      }
      if (value instanceof File || value instanceof Blob) {
        continue;
      }
      if (value !== undefined) {
        out[key] = value;
      }
    }
    return out;
  }

  isDraftCompatibleWithServer(
    draft: OpportunityFormDraft,
    serverSeed?: { id: string; status?: string; opportunityNumber?: string },
  ): boolean {
    if (draft.mode !== 'edit' || !draft.serverSeed || !serverSeed) {
      return true;
    }
    if (draft.serverSeed.id !== serverSeed.id) {
      return false;
    }
    const draftStatus = (draft.serverSeed.status ?? '').toUpperCase();
    const serverStatus = (serverSeed.status ?? '').toUpperCase();
    if (
      draftStatus &&
      serverStatus &&
      draftStatus !== serverStatus &&
      (serverStatus === 'ASSIGNED' || serverStatus === 'CONVERTED')
    ) {
      return false;
    }
    return true;
  }

  private sanitizeNavParams(params: OpportunityViewParams): OpportunityViewParams {
    const copy = { ...params } as Record<string, unknown>;
    for (const key of Object.keys(copy)) {
      if (SENSITIVE_FIELD_PATTERN.test(key)) {
        delete copy[key];
      }
    }
    return copy as OpportunityViewParams;
  }

  private sanitizeDraftValues(
    values: OpportunityFormDraftValues,
  ): OpportunityFormDraftValues {
    return {
      activeTab: values.activeTab,
      packPhoneOtpVerified: values.packPhoneOtpVerified === true ? true : undefined,
      packPreverifiedPhones: this.sanitizePackPreverifiedPhones(
        values.packPreverifiedPhones,
      ),
      packFormValues: values.packFormValues
        ? this.sanitizeFormFieldValues(values.packFormValues)
        : undefined,
      legacyForms: this.sanitizeLegacyFormsDraft(values.legacyForms),
    };
  }

  private sanitizePackPreverifiedPhones(
    packPreverifiedPhones: OpportunityFormDraftValues['packPreverifiedPhones'],
  ): Record<string, string> | undefined {
    if (!packPreverifiedPhones || typeof packPreverifiedPhones !== 'object') {
      return undefined;
    }
    const sanitized: Record<string, string> = {};
    for (const [key, mobile] of Object.entries(packPreverifiedPhones)) {
      if (!isPackPreverifiedPhoneEntry(key, mobile)) {
        continue;
      }
      sanitized[key] = mobile;
    }
    return Object.keys(sanitized).length > 0 ? sanitized : undefined;
  }

  private sanitizeLegacyFormsDraft(
    legacyForms: OpportunityFormDraftValues['legacyForms'],
  ): OpportunityFormDraftValues['legacyForms'] | undefined {
    if (!legacyForms) {
      return undefined;
    }
    const sanitizeSection = (section?: Record<string, unknown>) =>
      section ? this.sanitizeFormFieldValues(section) : undefined;
    return {
      basicDetails: sanitizeSection(legacyForms.basicDetails),
      loanDetails: sanitizeSection(legacyForms.loanDetails),
      sourceDetails: sanitizeSection(legacyForms.sourceDetails),
      addressDetails: sanitizeSection(legacyForms.addressDetails),
      leadPriority: sanitizeSection(legacyForms.leadPriority),
    };
  }

  private isExpired(updatedAt: number | undefined): boolean {
    if (!updatedAt) {
      return true;
    }
    return Date.now() - updatedAt > OPPORTUNITY_DRAFT_TTL_MS;
  }

  private writeJson(key: string, value: unknown): void {
    try {
      this.kv.set(key, JSON.stringify(value));
    } catch {
      // ignore
    }
  }

  private async readJson<T>(key: string): Promise<T | null> {
    try {
      const raw = await this.kv.get(key);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as T;
    } catch {
      this.removeKey(key);
      return null;
    }
  }

  private removeKey(key: string): void {
    this.kv.remove(key);
  }
}
